import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Skeleton } from "../ui/skeleton";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { formatCurrency } from "../../lib/utils";
import { BankIcon } from "../BankIcon";
import { ChevronDown, ChevronRight, ChevronUp, TrendingDown, TrendingUp, Wallet } from "lucide-react";

function maskCurrency(value, privacyMode, currency = "BRL") {
  return privacyMode ? "••••" : formatCurrency(value, currency);
}

function maskPercent(value, privacyMode) {
  if (privacyMode) return "••••";
  return new Intl.NumberFormat("pt-BR", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value || 0);
}

function ExpandButton({ expanded, onClick, expandLabel, collapseLabel }) {
  const ToggleIcon = expanded ? ChevronUp : ChevronDown;

  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      className="h-8 w-8 shrink-0"
      onClick={onClick}
      aria-label={expanded ? collapseLabel : expandLabel}
    >
      <ToggleIcon className="h-4 w-4" />
    </Button>
  );
}

function AccountBreakdownList({ items = [], privacyMode, getValue, getValueClassName, getAccountHref }) {
  return (
    <div className="mt-4 space-y-2 border-t pt-3">
      {items.length > 0 ? items.map((item) => {
        const value = getValue(item);
        const content = (
          <>
            <span className="flex min-w-0 items-center gap-2">
              <BankIcon iconId={item.account.bankIconId} bankName={item.account.bank} size="sm" />
              <span className="truncate text-sm font-medium">{item.account.name}</span>
            </span>
            <span className={`shrink-0 text-right font-mono text-sm font-semibold ${getValueClassName(value)} ${privacyMode ? "privacy-blur" : ""}`}>
              {maskCurrency(value, privacyMode, item.account.currency || "BRL")}
            </span>
          </>
        );
        const className = "flex min-w-0 items-center justify-between gap-3 rounded-md bg-muted/40 px-2 py-2";
        const href = getAccountHref?.(item);

        return href ? (
          <Link
            key={item.account.id || item.account.name}
            to={href}
            className={`${className} transition-colors hover:bg-muted`}
          >
            {content}
          </Link>
        ) : (
          <div key={item.account.id || item.account.name} className={className}>
            {content}
          </div>
        );
      }) : (
        <p className="rounded-md bg-muted/40 px-2 py-3 text-center text-sm text-muted-foreground">
          Nenhuma conta visível no dashboard
        </p>
      )}
    </div>
  );
}

function collectExpandableCategoryIds(items = []) {
  const ids = [];
  const visit = (node) => {
    if (node.children?.length) {
      ids.push(node.id);
      node.children.forEach(visit);
    }
  };

  items.forEach(visit);
  return ids;
}

function CategoryRow({
  item,
  total,
  mode,
  privacyMode,
  expandedIds,
  onToggle,
  valueClassName,
}) {
  const hasChildren = item.children?.length > 0;
  const expanded = expandedIds.has(item.id);
  const percent = total > 0 ? item.value / total : 0;
  const displayValue = mode === "percent"
    ? maskPercent(percent, privacyMode)
    : maskCurrency(item.value, privacyMode);

  return (
    <div>
      <div
        className="flex min-w-0 items-center justify-between gap-2 rounded-md bg-muted/40 px-2 py-2"
        style={{ marginLeft: `${Math.min(item.depth || 0, 3) * 12}px` }}
      >
        <div className="flex min-w-0 items-center gap-2">
          {hasChildren ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-6 w-6 shrink-0"
              onClick={() => onToggle(item.id)}
              aria-label={expanded ? `Recolher ${item.name}` : `Expandir ${item.name}`}
            >
              {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
            </Button>
          ) : (
            <span className="h-6 w-6 shrink-0" />
          )}
          <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: item.color }} />
          <span className={item.depth ? "truncate text-sm text-muted-foreground" : "truncate text-sm font-medium"}>
            {item.name}
          </span>
        </div>
        <span className={`shrink-0 text-right font-mono text-sm font-semibold ${valueClassName} ${privacyMode ? "privacy-blur" : ""}`}>
          {displayValue}
        </span>
      </div>
      {hasChildren && expanded && (
        <div className="mt-1 space-y-1">
          {item.children.map((child) => (
            <CategoryRow
              key={child.id}
              item={child}
              total={total}
              mode={mode}
              privacyMode={privacyMode}
              expandedIds={expandedIds}
              onToggle={onToggle}
              valueClassName={valueClassName}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CategoryBreakdownList({
  items = [],
  total = 0,
  mode,
  onModeChange,
  expandedIds,
  onToggleCategory,
  onToggleAll,
  privacyMode,
  valueClassName,
  emptyLabel,
}) {
  const expandableIds = collectExpandableCategoryIds(items);
  const allExpanded = expandableIds.length > 0 && expandableIds.every((id) => expandedIds.has(id));

  return (
    <div className="mt-4 space-y-3 border-t pt-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 rounded-md border bg-background px-2 py-1">
          <span className={`text-xs font-medium ${mode === "value" ? "text-foreground" : "text-muted-foreground"}`}>
            Valor
          </span>
          <Switch
            checked={mode === "percent"}
            onCheckedChange={(checked) => onModeChange(checked ? "percent" : "value")}
            aria-label="Alternar entre valor e percentual"
          />
          <span className={`text-xs font-medium ${mode === "percent" ? "text-foreground" : "text-muted-foreground"}`}>
            Percentual
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 px-2 text-xs"
          onClick={() => onToggleAll(allExpanded ? [] : expandableIds)}
          disabled={expandableIds.length === 0}
        >
          {allExpanded ? "Recolher todas" : "Expandir todas"}
        </Button>
      </div>

      {items.length > 0 ? (
        <div className="space-y-1">
          {items.map((item) => (
            <CategoryRow
              key={item.id}
              item={item}
              total={total}
              mode={mode}
              privacyMode={privacyMode}
              expandedIds={expandedIds}
              onToggle={onToggleCategory}
              valueClassName={valueClassName}
            />
          ))}
        </div>
      ) : (
        <p className="rounded-md bg-muted/40 px-2 py-3 text-center text-sm text-muted-foreground">
          {emptyLabel}
        </p>
      )}
    </div>
  );
}

export function BalanceCardsRow({
  accountBalances = [],
  totalIncome,
  totalExpense,
  incomeCategoryTree = [],
  expenseCategoryTree = [],
  realizadoOnly,
  getBalanceAccountHref,
  privacyMode,
  isLoading,
}) {
  const [expandedCards, setExpandedCards] = useState({
    balances: false,
    income: false,
    expense: false,
  });
  const [categoryDisplayModes, setCategoryDisplayModes] = useState({
    income: "value",
    expense: "value",
  });
  const [expandedCategoryIds, setExpandedCategoryIds] = useState({
    income: new Set(),
    expense: new Set(),
  });
  const totalBalance = useMemo(() => {
    return accountBalances.reduce((sum, item) => sum + (Number(item.balance) || 0), 0);
  }, [accountBalances]);
  const statusLabel = realizadoOnly ? "Realizado" : "Projetado";
  const toggleCard = (card) => {
    setExpandedCards((current) => ({
      ...current,
      [card]: !current[card],
    }));
  };
  const setCategoryMode = (card, mode) => {
    setCategoryDisplayModes((current) => ({ ...current, [card]: mode }));
  };
  const toggleCategory = (card, categoryId) => {
    setExpandedCategoryIds((current) => {
      const next = new Set(current[card]);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }

      return { ...current, [card]: next };
    });
  };
  const setAllCategoriesExpanded = (card, ids) => {
    setExpandedCategoryIds((current) => ({ ...current, [card]: new Set(ids) }));
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
      <Card className="balance-card card-hover md:col-span-1" data-testid="balance-card">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Wallet className="w-4 h-4" />
                Saldos de caixa
              </CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">Todas as contas</p>
            </div>
            <ExpandButton
              expanded={expandedCards.balances}
              onClick={() => toggleCard("balances")}
              expandLabel="Expandir saldos"
              collapseLabel="Recolher saldos"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? <Skeleton className="h-10 w-40" /> : (
            <>
              <p className={`text-3xl font-bold font-mono ${totalBalance < 0 ? "text-destructive" : "text-success"} ${privacyMode ? "privacy-blur" : "unscramble"}`}>
                {maskCurrency(totalBalance, privacyMode)}
              </p>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-success"></span>
                {statusLabel}
              </p>
              {expandedCards.balances && (
                <AccountBreakdownList
                  items={accountBalances}
                  privacyMode={privacyMode}
                  getValue={(item) => item.balance}
                  getValueClassName={(value) => (value < 0 ? "text-destructive" : "text-success")}
                  getAccountHref={getBalanceAccountHref || ((item) => `/account-statement?account_id=${encodeURIComponent(item.account.id)}`)}
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      <Card className="card-hover" data-testid="income-card">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-success" />
                Receitas
              </CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">Todas as contas</p>
            </div>
            <ExpandButton
              expanded={expandedCards.income}
              onClick={() => toggleCard("income")}
              expandLabel="Expandir receitas"
              collapseLabel="Recolher receitas"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? <Skeleton className="h-10 w-32" /> : (
            <>
              <p className={`text-2xl font-bold font-mono text-success ${privacyMode ? "privacy-blur" : ""}`}>
                {maskCurrency(totalIncome, privacyMode)}
              </p>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-success"></span>
                {statusLabel}
              </p>
              {expandedCards.income && (
                <CategoryBreakdownList
                  items={incomeCategoryTree}
                  total={totalIncome}
                  mode={categoryDisplayModes.income}
                  onModeChange={(mode) => setCategoryMode("income", mode)}
                  expandedIds={expandedCategoryIds.income}
                  onToggleCategory={(categoryId) => toggleCategory("income", categoryId)}
                  onToggleAll={(ids) => setAllCategoriesExpanded("income", ids)}
                  privacyMode={privacyMode}
                  valueClassName="text-success"
                  emptyLabel="Nenhuma receita por categoria"
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
      
      <Card className="card-hover" data-testid="expense-card">
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <TrendingDown className="w-4 h-4 text-destructive" />
                Despesas
              </CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">Todas as contas</p>
            </div>
            <ExpandButton
              expanded={expandedCards.expense}
              onClick={() => toggleCard("expense")}
              expandLabel="Expandir despesas"
              collapseLabel="Recolher despesas"
            />
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? <Skeleton className="h-10 w-32" /> : (
            <>
              <p className={`text-2xl font-bold font-mono text-destructive ${privacyMode ? "privacy-blur" : ""}`}>
                {maskCurrency(totalExpense, privacyMode)}
              </p>
              <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                <span className="w-1.5 h-1.5 rounded-full bg-success"></span>
                {statusLabel}
              </p>
              {expandedCards.expense && (
                <CategoryBreakdownList
                  items={expenseCategoryTree}
                  total={totalExpense}
                  mode={categoryDisplayModes.expense}
                  onModeChange={(mode) => setCategoryMode("expense", mode)}
                  expandedIds={expandedCategoryIds.expense}
                  onToggleCategory={(categoryId) => toggleCategory("expense", categoryId)}
                  onToggleAll={(ids) => setAllCategoriesExpanded("expense", ids)}
                  privacyMode={privacyMode}
                  valueClassName="text-destructive"
                  emptyLabel="Nenhuma despesa por categoria"
                />
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
